/**
 * useAlertRules.ts
 * Feature: 125 - Alert Rule Management
 * Author: Peter Dobler
 * Date: 2026-03-25
 * Constitution Version: 1.12.0
 *
 * Purpose: React Query hooks for alert rule CRUD operations.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import type {
  AlertRuleView,
  AlertRuleListResponse,
  AlertRuleFilters,
  PaginationParams,
  SortParams,
  CreateAlertRuleRequest,
  UpdateAlertRuleRequest,
  ToggleAlertRuleRequest,
  ValidateNameRequest,
  ValidateNameResponse,
  AlertTypeOption,
  SeverityOption,
  AddAssignmentRequest,
} from '@shared/types/alertRuleManagement';
import {
  fetchAlertRules,
  fetchAlertRuleById,
  createAlertRule,
  updateAlertRule,
  toggleAlertRule,
  deleteAlertRule,
  validateAlertRuleName,
  fetchAlertTypes,
  fetchSeverities,
  fetchRuleAssignments,
  addRuleAssignment,
  removeRuleAssignment,
} from '../services/alertRulesApi';

// =============================================================================
// Query Keys
// =============================================================================

export const alertRuleKeys = {
  all: ['alert-rules'] as const,
  lists: () => [...alertRuleKeys.all, 'list'] as const,
  list: (filters: AlertRuleFilters, pagination: PaginationParams, sort: SortParams) =>
    [...alertRuleKeys.lists(), { filters, pagination, sort }] as const,
  details: () => [...alertRuleKeys.all, 'detail'] as const,
  detail: (id: string) => [...alertRuleKeys.details(), id] as const,
  types: () => [...alertRuleKeys.all, 'types'] as const,
  severities: () => [...alertRuleKeys.all, 'severities'] as const,
  assignments: (id: string) => [...alertRuleKeys.all, 'assignments', id] as const,
};

// =============================================================================
// List Alert Rules Hook
// =============================================================================

export interface UseAlertRulesOptions {
  filters?: AlertRuleFilters;
  pagination?: PaginationParams;
  sort?: SortParams;
  enabled?: boolean;
}

/**
 * Hook to fetch paginated list of alert rules with filtering
 */
export function useAlertRules(options: UseAlertRulesOptions = {}) {
  const {
    filters = {},
    pagination = { page: 1, pageSize: 25 },
    sort = { sortBy: 'name', sortOrder: 'asc' },
    enabled = true,
  } = options;

  return useQuery({
    queryKey: alertRuleKeys.list(filters, pagination, sort),
    queryFn: () => fetchAlertRules({ filters, pagination, sort }),
    enabled,
    staleTime: 30000, // 30 seconds
    placeholderData: (previousData) => previousData,
  });
}

// =============================================================================
// Get Alert Rule Hook
// =============================================================================

/**
 * Hook to fetch a single alert rule by ID
 */
export function useAlertRule(
  id: string | undefined,
  options?: Omit<UseQueryOptions<AlertRuleView | null>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: alertRuleKeys.detail(id ?? ''),
    queryFn: () => (id ? fetchAlertRuleById(id) : Promise.resolve(null)),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 30000,
    ...options,
  });
}

// =============================================================================
// Create Alert Rule Hook
// =============================================================================

/**
 * Hook to create a new alert rule
 */
export function useCreateAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAlertRuleRequest) => createAlertRule(data),
    onSuccess: () => {
      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.lists() });
    },
  });
}

// =============================================================================
// Update Alert Rule Hook
// =============================================================================

/**
 * Hook to update an existing alert rule
 */
export function useUpdateAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAlertRuleRequest }) =>
      updateAlertRule(id, data),
    onSuccess: (updatedRule) => {
      // Update the cache for this specific rule
      if (updatedRule) {
        queryClient.setQueryData(alertRuleKeys.detail(updatedRule.id), updatedRule);
      }
      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.lists() });
    },
  });
}

// =============================================================================
// Toggle Alert Rule Hook
// =============================================================================

/**
 * Hook to toggle alert rule enabled status
 */
export function useToggleAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      toggleAlertRule(id, { enabled }),
    onMutate: async ({ id, enabled }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: alertRuleKeys.lists() });

      // Snapshot the previous value
      const previousLists = queryClient.getQueriesData({ queryKey: alertRuleKeys.lists() });

      // Optimistically update all list queries
      queryClient.setQueriesData<AlertRuleListResponse>(
        { queryKey: alertRuleKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((rule) =>
              rule.id === id ? { ...rule, enabled } : rule
            ),
          };
        }
      );

      return { previousLists };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch after mutation
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.lists() });
    },
  });
}

// =============================================================================
// Delete Alert Rule Hook
// =============================================================================

/**
 * Hook to delete an alert rule
 */
export function useDeleteAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAlertRule(id),
    onSuccess: (_data, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: alertRuleKeys.detail(deletedId) });
      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.lists() });
    },
  });
}

// =============================================================================
// Validate Name Hook
// =============================================================================

/**
 * Hook to validate alert rule name uniqueness
 */
export function useValidateAlertRuleName() {
  return useMutation({
    mutationFn: (data: ValidateNameRequest) => validateAlertRuleName(data),
  });
}

// =============================================================================
// Reference Data Hooks
// =============================================================================

/**
 * Hook to fetch available alert types
 */
export function useAlertTypes() {
  return useQuery({
    queryKey: alertRuleKeys.types(),
    queryFn: fetchAlertTypes,
    staleTime: Infinity, // Reference data doesn't change
  });
}

/**
 * Hook to fetch available severity levels
 */
export function useSeverities() {
  return useQuery({
    queryKey: alertRuleKeys.severities(),
    queryFn: fetchSeverities,
    staleTime: Infinity, // Reference data doesn't change
  });
}

// =============================================================================
// Spec 192 — Assignment management hooks (FR-010)
// =============================================================================

/**
 * Hook to fetch a rule's capacity/lakehouse assignments (entity names + scope).
 */
export function useRuleAssignments(id: string | undefined) {
  return useQuery({
    queryKey: alertRuleKeys.assignments(id ?? ''),
    queryFn: () => fetchRuleAssignments(id as string),
    enabled: !!id,
    staleTime: 15000,
  });
}

/**
 * Hook to add a capacity/lakehouse assignment to a rule (idempotent).
 */
export function useAddRuleAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, target }: { id: string; target: AddAssignmentRequest }) =>
      addRuleAssignment(id, target),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.assignments(id) });
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.lists() }); // scope column
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.detail(id) });
    },
  });
}

/**
 * Hook to remove an assignment. The response carries the post-removal scope hint
 * ('unassigned' for per-target rules — never silently global).
 */
export function useRemoveRuleAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, target }: { id: string; target: { capacityId?: string; lakehouseId?: string } }) =>
      removeRuleAssignment(id, target),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.assignments(id) });
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: alertRuleKeys.detail(id) });
    },
  });
}
